/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useMemo, useCallback } from 'react';
import { Grid, Divider, FormControlLabel, Radio } from '@mui/material';
import { getApiConfigSafe } from '../../../utils/api-config';
import DynamicInput from '../form-builder/form-builder-element/text';
import DynamicDate from '../form-builder/form-builder-element/date';
import DynamicTime from '../form-builder/form-builder-element/time';
import DynamicPhone from '../form-builder/form-builder-element/phone';
import DynamicSearchSelect from '../form-builder/form-builder-element/dynamic-select';
import DynamicDependentField from '../form-builder/form-builder-element/dynamic-dependent-field';
import DynamicMedia from '../form-builder/form-builder-element/media';
import DynamicInfo from '../form-builder/form-builder-element/info';
import DynamicToggleButton from '../form-builder/form-builder-element/toggle-button';
import DynamicCheckBox from '../form-builder/form-builder-element/checkbox';
import DynamicTable from '../form-builder/form-builder-element/table';
import DynamicRadioButton from '../form-builder/form-builder-element/radio-button';
import DynamicTagsInput from '../form-builder/form-builder-element/dynamic-tags-input';
import DynamicCurrency from '../form-builder/form-builder-element/dynamic-currency-input';
import DynamicInputSelect from '../form-builder/form-builder-element/dynamic-input-select';
import ChipGenerator from '../../chip-generator/chip-generator';
import { LocationSearchSelect } from '../../location-select';
import FormSwitcher from './form-switcher';
import Typography from '../../typography/typography';
import Accordion from '../../accordion/accordion';

// Types for better type safety
interface Field {
  name?: string;
  label: string;
  field_type: string;
  is_fullwidth?: boolean;
  custom_width?: number;
  is_hidden?: boolean;
  is_required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: any;
  default_value?: any;
  title?: string;
  title_position?: string;
  table_columns?: any[];
  table_rows?: any[];
  component?: React.ReactNode;
  field_properties?: {
    apiType?: string;
    customeFilter?: any;
    filterFields?: string | string[];
    attributes?: any;
    disabledIds?: any[];
    sortOrder?: string;
    maxChips?: number;
    allowDuplicates?: boolean;
  };
  validation?: any;
  filterField?: string;
}

interface Section {
  label?: string;
  is_accordion?: boolean;
  disabled?: boolean;
  form_switcher_label?: string;
}

interface FormSectionProps {
  section: Section;
  index: number;
  members?: Field[];
  switchForms?: any[];
  isFormSwitcher: boolean;
  control: any;
  fieldArrayName: string;
  formSwitcherName: string;
  displayAvtar?: boolean;
  isButton?: boolean;
  currencyOption?: any;
  values: any;
  onBlur?: (e: any, fieldName?: string) => void;
  setValue: (name: string, value: any) => void;
  onSelectFooterClick?: (field: Field) => void;
  getSelectedData?: (row: any, key: any) => void;
  setCurrencyOptions: (options: any) => void;
  refreshOptions: any;
  handleFieldCleanUp: (forms: any[]) => void;
  handleSetCountryCodeField: (country: any, field: Field, fieldArrayName: string, values: any) => void;
  handleTableAddClick: (field: Field) => void;
  onTableCellChange?: (data: any) => void;
  // setDetailsData: (data: any) => void;
  t: (key: string) => string;
  images: any;
}

// Memoized field component to prevent unnecessary re-renders
const MemoizedField = memo(({
  field,
  fieldName,
  sectionLabel,
  index,
  ...props
}: {
  field: Field;
  fieldName: string;
  sectionLabel?: string;
  index: number;
  [key: string]: any;
}) => {
  const isSpar = getApiConfigSafe()?.countryEnv === 'spar';
  const gridSize = useMemo(() =>
    field.is_fullwidth ? 12 : (field.custom_width ?? 6),
    [field.is_fullwidth, field.custom_width]
  );

  const fieldKey = useMemo(() =>
    `${sectionLabel}-${fieldName}-${index}`,
    [sectionLabel, fieldName, index]
  );

  const commonProps = useMemo(() => ({
    ...field,
    key: fieldKey,
    name: fieldName,
    position: index,
    formControl: props.control,
    fieldArrayName: props.fieldArrayName,
    required: field.is_required,
    label: props.t(field.label),
    placeholder: props.t(field.placeholder),
    defaultValue: field.value || field.default_value,
    disabled: field.disabled,
    ...(isSpar && field.field_properties?.float_step ? { float_step: field.field_properties.float_step } : {})
  }), [field, fieldName, index, fieldKey, props.control, props.fieldArrayName, props.t]);

  const handleBlur = useCallback((e: any) => {
    if (props.onBlur && typeof props.onBlur === 'function') {
      props.onBlur(e, fieldName);
    }
  }, [props.onBlur, fieldName]);

  const endIcon = useMemo(() => {
    const iconMap: Record<string, string> = {
      email: props.images.sms,
      url: props.images.url,
      currency: props.images.dollar,
      geoLocation: props.images.location
    };

    const iconSrc = iconMap[field.field_type];
    return iconSrc ? <img src={iconSrc} alt={field.field_type} /> : null;
  }, [field.field_type, props.images]);

  const relationLabel = useMemo(() => (
    field.field_type === "relation" ? (
      <span className="formSection--Relation">
        {props.t(field.label)}
        <img src={props.images.relation} alt="relation" />
      </span>
    ) : props.t(field.label)
  ), [field.field_type, field.label, props.t, props.images.relation]);

  // Render field based on type
  const renderField = () => {
    switch (field.field_type) {
      case "text":
      case "email":
      case "url":
      case "currency":
      case "number":
      case "geoLocation":
      case "relation":
        return (
          <DynamicInput
            {...commonProps}
            key={commonProps.key}
            label={relationLabel}
            onBlur={handleBlur}
            type={field.field_type}
            endIcon={endIcon}
            disabled={field.disabled || field.field_type === "relation"}
          />
        );

      case "date":
        return <DynamicDate {...commonProps} key={commonProps.key} />;

      case "time":
        return <DynamicTime {...commonProps} key={commonProps.key} />;

      case "phone":
        return (
          <DynamicPhone
            {...commonProps}
            key={commonProps.key}
            setCountryCodeField={(country: any) =>
              props.handleSetCountryCodeField(country, field, props.fieldArrayName, props.values)
            }
          />
        );

      case "select":
        return (
          <DynamicSearchSelect
            {...commonProps}
            key={commonProps.key}
            apiType={field.field_properties?.apiType}
            customeFilter={field.field_properties?.customeFilter}
            attributes={field.field_properties?.attributes}
            disabledIds={field.field_properties?.disabledIds}
            onChipRemove={() => props.setValue(`${props.fieldArrayName}.${fieldName}`, [])}
            {
            ...(!field?.addType && !field?.field_properties?.addType
              ? { onFooterClick: () => props.onSelectFooterClick?.(field) }
              : {})
            }
            handleOptions={props.setCurrencyOptions}
            getSelectedData={props.getSelectedData}
            refresh={props.refreshOptions}
            sortOrder={field.field_properties?.sortOrder}
            showCancelButton={field.field_properties?.showCancelButton}
            addType={field?.addType || field?.field_properties?.addType}
            modalComponentProps={field?.field_properties?.modalComponentProps}
          />
        );

      case "dynamic-dependent-select":
        return (
          <DynamicDependentField
            {...commonProps}
            key={commonProps.key}
            apiType={field.field_properties?.apiType}
            customeFilter={field.field_properties?.customeFilter}
            filterFields={field.field_properties?.filterFields}
            attributes={field.field_properties?.attributes}
            disabledIds={field.field_properties?.disabledIds}
            onChipRemove={() => props.setValue(`${props.fieldArrayName}.${fieldName}`, [])}
            {
            ...(!field?.addType && !field?.field_properties?.addType
              ? { onFooterClick: () => props.onSelectFooterClick?.(field) }
              : {})
            }
            handleOptions={props.setCurrencyOptions}
            getSelectedData={props.getSelectedData}
            refresh={props.refreshOptions}
            sortOrder={field.field_properties?.sortOrder}
            showCancelButton={field.field_properties?.showCancelButton}
            addType={field?.addType || field?.field_properties?.addType}
            modalComponentProps={field?.field_properties?.modalComponentProps}
          />
        );

      case "file":
      case "image":
        return <DynamicMedia {...commonProps} key={commonProps.key} />;

      case "info":
        return <DynamicInfo {...commonProps} key={commonProps.key} />;

      case "toggleButton":
        return (
          <DynamicToggleButton
            {...commonProps}
            key={commonProps.key}
            titlePosition={field.title_position}
            title={props.t(field.title)}
          />
        );

      case "checkbox":
        return (
          <DynamicCheckBox
            {...commonProps}
            key={commonProps.key}
            titlePosition={field.title_position}
            title={props.t(field.title)}
          />
        );

      case "table":
        return (
          <DynamicTable
            {...commonProps}
            key={commonProps.key}
            columns={field.table_columns}
            rows={field.table_rows}
            onAddClick={() => props.handleTableAddClick(field)}
            disableTableAddButton={Boolean(field.disableTableAddButton)}
            is_button={props.isButton}
            onTableCellChange={props.onTableCellChange}
          />
        );

      case "radioButton":
        return (
          <DynamicRadioButton
            {...commonProps}
            key={commonProps.key}
            titlePosition={field.title_position}
            title={props.t(field.title)}
          />
        );

      case "customComponent":
        return field.component;

      case "tagsInput":
        return (
          <DynamicTagsInput
            {...commonProps}
            key={commonProps.key}
            label={relationLabel}
            type={field.field_type}
            endIcon={endIcon}
          />
        );

      case "currency_with_amount":
        return (
          <DynamicCurrency
            {...commonProps}
            key={commonProps.key}
            currencyType={props.currencyOption}
          />
        );

      case "select_with_input":
        return (
          <DynamicInputSelect
            {...commonProps}
            currencyType={props.currencyOption}
          />
        );

      case "chipInput":
        return (
          <ChipGenerator
            {...commonProps}
            key={commonProps.key}
            maxChips={field.field_properties?.maxChips}
            allowDuplicates={field.field_properties?.allowDuplicates}
            validation={field.validation}
            endIcon={endIcon}
            onChipAdd={(chip: any) => console.log('Chip added:', chip)}
            onChipRemove={(chip: any, index: number) => console.log('Chip removed:', chip, 'at index:', index)}
          />
        );

      case "location_select":
        return (
          <LocationSearchSelect
            fieldArrayName={props.fieldArrayName}
            filterField={field.filterField}
            getSelectedData={(row: any[]) => props.getSelectedData(row)}
            locationFieldName={field.name}
            addType="location"
            enable_footer={true}
          />
        );

      default:
        return null;
    }
  };

  // For table fields, use full width
  if (field.field_type === "table" || field.field_type === "customComponent") {
    return (
      <Grid item xs={12}>
        {renderField()}
      </Grid>
    );
  }

  return (
    <Grid item xs={gridSize}>
      {renderField()}
    </Grid>
  );
});

MemoizedField.displayName = 'MemoizedField';

// Memoized FormSwitcher component
const MemoizedFormSwitcher = memo(({
  section,
  switchForms,
  isFormSwitcher,
  control,
  fieldArrayName,
  formSwitcherName,
  handleFieldCleanUp,
  t
}: {
  section: Section;
  switchForms?: any[];
  isFormSwitcher: boolean;
  control: any;
  fieldArrayName: string;
  formSwitcherName: string;
  handleFieldCleanUp: (forms: any[]) => void;
  t: (key: string) => string;
}) => {
  const resetForm = useCallback(() => {
    handleFieldCleanUp(switchForms || []);
  }, [handleFieldCleanUp, switchForms]);

  if (!isFormSwitcher || !switchForms?.length) return null;

  return (
    <FormSwitcher
      {...section}
      isFormSwitcher={isFormSwitcher}
      control={control}
      fieldArrayName={fieldArrayName}
      formSwitcherName={formSwitcherName}
      label={t(section.form_switcher_label || '')}
      resetForm={resetForm}
    >
      {switchForms.map((forms: any) => (
        <FormControlLabel
          key={forms.id}
          value={forms.value || forms.name}
          control={<Radio />}
          slotProps={{ typography: { variant: "body2" } }}
          label={t(forms.name)}
          disabled={Boolean(section.disabled)}
        />
      ))}
    </FormSwitcher>
  );
});

MemoizedFormSwitcher.displayName = 'MemoizedFormSwitcher';

// Main component
const FormSection = memo<FormSectionProps>(({
  section,
  index,
  members,
  switchForms,
  isFormSwitcher,
  control,
  fieldArrayName,
  formSwitcherName,
  displayAvtar,
  isButton,
  currencyOption,
  values,
  onBlur,
  setValue,
  onSelectFooterClick,
  getSelectedData,
  setCurrencyOptions,
  refreshOptions,
  handleFieldCleanUp,
  handleSetCountryCodeField,
  handleTableAddClick,
  onTableCellChange,
  setDetailsData,
  t,
  images
}) => {
  // Memoize visible members to prevent recalculation
  const visibleMembers = useMemo(() =>
    members?.filter(f => !f.is_hidden) || [],
    [members]
  );

  // Memoize field names to prevent recalculation
  const fieldsWithNames = useMemo(() =>
    visibleMembers.map(field => ({
      ...field,
      fieldName: field.name || field.label.toLowerCase().split(" ").join("_")
    })),
    [visibleMembers]
  );

  // Memoize section header
  const sectionHeader = useMemo(() => {
    if (!section.label) return null;

    return (
      <div style={{ minHeight: "16px" }}>
        {index !== 0 && <Divider className='formParser--Section--SectionDivider' />}
        <Typography
          type="s3"
          weight="medium"
          mb={2}
          mt={4}
          color="theme.secondary.1000"
          className='formParser--Section--SectionTitle'
        >
          {t(section.label)}
        </Typography>
      </div>
    );
  }, [section.label, index, t]);

  // Avatar component
  const avatarDisplay = useMemo(() =>
    displayAvtar ? <div className='avtarDisplay'>ERP</div> : null,
    [displayAvtar]
  );

  // Common props for field rendering
  const fieldProps = useMemo(() => ({
    control,
    fieldArrayName,
    isButton,
    currencyOption,
    values,
    onBlur,
    setValue,
    onSelectFooterClick,
    getSelectedData,
    setCurrencyOptions,
    refreshOptions,
    handleSetCountryCodeField,
    handleTableAddClick,
    onTableCellChange,
    setDetailsData,
    t,
    images
  }), [
    control, fieldArrayName, isButton, currencyOption, values,
    onBlur, setValue, onSelectFooterClick, getSelectedData,
    setCurrencyOptions, refreshOptions, handleSetCountryCodeField,
    handleTableAddClick, onTableCellChange, setDetailsData, t, images
  ]);

  if (section.is_accordion) {
    return (
      <section key={index} className='formParser--Section'>
        <div style={{ marginTop: '20px' }}>
          <Accordion title={t(section.label || '')}>
            <div className="formParser--Grid formParser--Accordion">
              <MemoizedFormSwitcher
                section={section}
                switchForms={switchForms}
                isFormSwitcher={isFormSwitcher}
                control={control}
                fieldArrayName={fieldArrayName}
                formSwitcherName={formSwitcherName}
                handleFieldCleanUp={handleFieldCleanUp}
                t={t}
              />
              {fieldsWithNames.map((field, fieldIndex) => (
                <MemoizedField
                  key={`${section.label}-${field.fieldName}-${fieldIndex}`}
                  field={field}
                  fieldName={field.fieldName}
                  sectionLabel={section.label}
                  index={fieldIndex}
                  {...fieldProps}
                />
              ))}
            </div>
          </Accordion>
        </div>
      </section>
    );
  }

  return (
    <section key={index} className='formParser--Section'>
      {sectionHeader}

      <MemoizedFormSwitcher
        section={section}
        switchForms={switchForms}
        isFormSwitcher={isFormSwitcher}
        control={control}
        fieldArrayName={fieldArrayName}
        formSwitcherName={formSwitcherName}
        handleFieldCleanUp={handleFieldCleanUp}
        t={t}
      />

      <Grid container spacing={2}>
        {avatarDisplay}
        {fieldsWithNames.map((field, fieldIndex) => (
          <MemoizedField
            key={`${section.label}-${field.fieldName}-${fieldIndex}`}
            field={field}
            fieldName={field.fieldName}
            sectionLabel={section.label}
            index={fieldIndex}
            {...fieldProps}
          />
        ))}
      </Grid>
    </section>
  );
});

FormSection.displayName = 'FormSection';

export { FormSection };
export default FormSection;